import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { PaletteMode } from '@mui/material';

// Constants
const STORAGE_KEY = 'themeMode';
const DEFAULT_THEME: PaletteMode = 'light';

/**
 * Theme context type definition
 */
interface ThemeContextType {
  mode: PaletteMode;
  toggleTheme: () => void;
  setTheme: (mode: PaletteMode) => void;
  isDarkMode: boolean;
}

/**
 * Get the initial theme from localStorage or use default
 */
const getInitialTheme = (): PaletteMode => {
  try {
    const savedMode = localStorage.getItem(STORAGE_KEY);
    // Validate that the saved mode is a valid PaletteMode
    return (savedMode === 'light' || savedMode === 'dark') 
      ? savedMode 
      : DEFAULT_THEME;
  } catch (error) {
    // In case of any localStorage errors, return default
    console.error('Error accessing localStorage for theme:', error);
    return DEFAULT_THEME;
  }
};

// Create context with default values
const ThemeContext = createContext<ThemeContextType>({
  mode: DEFAULT_THEME,
  toggleTheme: () => {},
  setTheme: () => {},
  isDarkMode: false
});

// Custom hook for using the theme context
export const useTheme = () => useContext(ThemeContext);

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Theme Provider Component
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Initialize theme from localStorage or default
  const [mode, setMode] = useState<PaletteMode>(getInitialTheme);

  /**
   * Set theme to a specific mode
   */
  const setTheme = useCallback((newMode: PaletteMode) => {
    setMode(newMode);
    localStorage.setItem(STORAGE_KEY, newMode);
  }, []);

  /**
   * Toggle between light and dark modes
   */
  const toggleTheme = useCallback(() => {
    setMode((prevMode) => {
      const newMode = prevMode === 'light' ? 'dark' : 'light';
      localStorage.setItem(STORAGE_KEY, newMode);
      return newMode;
    });
  }, []);

  // Derived state
  const isDarkMode = mode === 'dark';

  // Context value
  const contextValue: ThemeContextType = {
    mode,
    toggleTheme,
    setTheme,
    isDarkMode
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};